// Configuração de navegação compartilhada entre a sidebar (desktop) e a bottom bar
// (mobile). Fonte única dos destinos para não divergirem — DRY.
import type { LinkProps } from "@tanstack/react-router";
import {
  Activity,
  BarChart3,
  Bot,
  CircleQuestionMark,
  Compass,
  Gauge,
  Info,
  LayoutDashboard,
  LifeBuoy,
  ListOrdered,
  MousePointerClick,
  Phone,
  PhoneOutgoing,
  Plug,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { canAccess } from "./lib/access.js";
import type { PermissionCatalog } from "./api.js";
import type { ptBR } from "./locales/pt-BR.js";

type NavigationKey = keyof typeof ptBR.navigation;

const NAVIGATION_KEYS: Record<string, NavigationKey> = {
  Monitoramento: "monitoring",
  "Chamadas em andamento": "activeCalls",
  "Saúde das ligações": "callHealth",
  Relatórios: "reports",
  "Visão geral": "overview",
  "Resultados do dia": "dailyResults",
  Ligações: "calls",
  "Números inválidos": "invalidNumbers",
  Preditivo: "predictive",
  Cadência: "cadence",
  Finalizações: "finalizations",
  Operadores: "sellers",
  Espera: "wait",
  "Taxa de atendimento": "attendance",
  "Movimentação de leads": "leadFlow",
  "Todas as marcas": "allBrands",
  Gestão: "management",
  Leads: "leads",
  Usuários: "users",
  "Papéis e permissões": "roles",
  Times: "teams",
  "Material de apoio": "supportMaterial",
  "Áudios de atendimento": "ura",
  "Formulário pós-ligação": "postCallForm",
  Auditoria: "audit",
  "Monitoramento de acesso": "accessMonitoring",
  "Privacidade e retenção": "privacyRetention",
  "Click to call": "clickToCall",
  Geral: "general",
  Relatório: "report",
  Saúde: "health",
  "IA de voz": "voiceAi",
  "Roteiro da IA": "aiScript",
  "Voz da IA": "aiVoice",
  "Avaliações da IA": "aiEvaluations",
  Financeiro: "financial",
  Plano: "plan",
  Pagamento: "payment",
  Consumo: "consumption",
  "Custo por Minutagem": "minuteCost",
  "Custo por usuário": "userCost",
  Fila: "queue",
  Filas: "queueRule",
  Quadro: "board",
  Funil: "funnel",
  "Etapas de conversão": "conversionStages",
  "Marcos do funil": "leadFlowMilestones",
  Integrações: "integrations",
  Automações: "automations",
  Marketplace: "marketplace",
  "Integração MCP": "mcpIntegration",
  Ingestão: "ingestion",
  "Logs de integração": "integrationLogs",
  Configurações: "settings",
  Empresa: "company",
  "Identidade da marca": "brandIdentity",
  "Horário de atendimento": "businessHours",
  Discador: "dialer",
  "Meu desempenho": "myPerformance",
  Desempenho: "performance",
  Assistente: "assistant",
  Dashboard: "dashboard",
  "Discador automático": "automaticDialer",
  "Discagem manual": "manualDialer",
  Sobre: "about",
  "Comece por aqui": "startHere",
  Ajuda: "help",
  "Central de Ajuda": "help",
  "Meus chamados": "myTickets",
  Suporte: "support",
  "Instalar app": "installApp",
};

export function navTranslationKey(label: string): `navigation.${NavigationKey}` | null {
  const key = NAVIGATION_KEYS[label];
  return key ? `navigation.${key}` : null;
}

export type NavLeaf = { to: LinkProps["to"]; label: string };
export type NavGroupDef = { label: string; icon: LucideIcon; items: NavLeaf[] };
export type NavLink = { to: LinkProps["to"]; label: string; icon: LucideIcon };

export const MONITORAMENTO: NavGroupDef = {
  label: "Monitoramento",
  icon: Activity,
  items: [
    { to: "/monitoring/active-calls", label: "Chamadas em andamento" },
    { to: "/reports/call-health", label: "Saúde das ligações" },
    // "Ramais SIP" (/monitoring/sip-extensions) também escondida. Rota mantida
    // para links diretos.
    // "Linha de saída" (/monitoring/trunks) escondida da navegação: os mesmos
    // dados e configurações vivem em Integrações › Telefonia. O link antigo redireciona.
    // "Filas de atendimento" também fica oculta temporariamente. A rota
    // /monitoring/queues permanece válida para links diretos.
  ],
};

export const RELATORIOS: NavGroupDef = {
  label: "Relatórios",
  icon: BarChart3,
  items: [
    { to: "/reports", label: "Visão geral" },
    { to: "/reports/daily-results", label: "Resultados do dia" },
    { to: "/reports/calls", label: "Ligações" },
    { to: "/reports/calls/invalid-numbers", label: "Números inválidos" },
    { to: "/reports/predictive", label: "Preditivo" },
    // "Filas" (/reports/queues) escondida da navegação. A rota continua
    // registrada e válida para links diretos — só não é mais alcançável pela
    // sidebar/bottom bar.
    { to: "/reports/cadence", label: "Cadência" },
    { to: "/reports/finalizations", label: "Finalizações" },
    // "Tabulações" (/reports/dispositions) escondida da navegação. A rota
    // continua registrada e válida para links diretos — só não é mais
    // alcançável pela sidebar/bottom bar.
    { to: "/reports/sellers", label: "Operadores" },
    { to: "/reports/attendance", label: "Taxa de atendimento" },
    { to: "/reports/lead-flow", label: "Movimentação de leads" },
    { to: "/reports/wait", label: "Espera" },
    { to: "/reports/all-brands", label: "Todas as marcas" },
  ],
};

export const GESTAO: NavGroupDef = {
  label: "Gestão",
  icon: Users,
  items: [
    { to: "/management/leads", label: "Leads" },
    { to: "/management/users", label: "Usuários" },
    { to: "/management/roles", label: "Papéis e permissões" },
    { to: "/management/teams", label: "Times" },
    { to: "/management/support-material", label: "Material de apoio" },
    // "Tabulações" (/management/dispositions) escondida da navegação. A rota
    // continua registrada e válida para links diretos — só não é mais
    // alcançável pela sidebar/bottom bar.
    { to: "/management/ura", label: "Áudios de atendimento" },
    { to: "/management/call-form", label: "Formulário pós-ligação" },
  ],
};

// "Preditivo" saiu daqui: a tabela tentativa a tentativa passou a ser o último
// bloco de Relatórios › Preditivo, junto dos números que ela explica. Com o
// click-to-call e os logs de integração já migrados para os grupos próprios,
// sobra só o monitoramento de acesso — o grupo fica de um item por ora.
export const AUDITORIA: NavGroupDef = {
  label: "Auditoria",
  icon: ShieldCheck,
  items: [
    { to: "/management/access", label: "Monitoramento de acesso" },
    { to: "/auditing/privacy", label: "Privacidade e retenção" },
  ],
};

// Click to call: GRUPO PRÓPRIO da navegação. As três telas eram itens colados
// no fim de Integrações, onde ficavam longe do nome do assunto e misturadas com
// CRM, MCP e logs. Em Integrações sobra só a tela de ligar/desligar — que é o
// que de fato é uma integração a configurar; a operação do canal (o geral, a
// relatório, auditoria e a saúde) mora aqui.
export const CLICK_TO_CALL: NavGroupDef = {
  label: "Click to call",
  icon: MousePointerClick,
  items: [
    { to: "/management/click-to-call", label: "Geral" },
    { to: "/management/click-to-call-report", label: "Relatório" },
    // Consolidado das marcas da company, só com acesso cross-marca (canAccess).
    { to: "/management/click-to-call-all-brands", label: "Todas as marcas" },
    { to: "/management/click-to-call-audit", label: "Auditoria" },
    { to: "/management/click-to-call-health", label: "Saúde" },
  ],
};

// "IA de voz": o dropdown que reúne tudo da IA de voz — o roteiro (Compass), a
// voz da org e as avaliações. Substitui o antigo link solto /voice (a config
// global e os scripts do talk saíram no voice-talk stateless). Os gastos
// saíram daqui para o grupo Financeiro.
export const CONTROLE_IA: NavGroupDef = {
  label: "IA de voz",
  icon: Bot,
  items: [
    // Roteiros pausados junto com a integração Compass no backend.
    { to: "/management/voices", label: "Voz da IA" },
    { to: "/management/ai-evaluations", label: "Avaliações da IA" },
  ],
};

// Financeiro tem três perguntas, uma por tela: qual é o plano, o que foi
// consumido e o que foi/precisa ser pago.
export const FINANCEIRO: NavGroupDef = {
  label: "Financeiro",
  icon: Wallet,
  items: [
    { to: "/financial/plan", label: "Plano" },
    { to: "/financial/payment", label: "Pagamento" },
    { to: "/financial/consumption", label: "Consumo" },
  ],
};

// Fila: como o discador decide para quem tocar e o que conta como conversão.
// Os dois itens saíram de Gestão para ficarem juntos aqui.
export const FILA: NavGroupDef = {
  label: "Fila",
  icon: ListOrdered,
  items: [
    { to: "/management/queue-config", label: "Filas" },
    { to: "/management/queue-kanban", label: "Quadro" },
    { to: "/management/funnels", label: "Funil" },
    { to: "/management/conversion-stages", label: "Etapas de conversão" },
    { to: "/management/lead-flow-milestones", label: "Marcos do funil" },
  ],
};

export const INTEGRACOES: NavGroupDef = {
  label: "Integrações",
  icon: Plug,
  items: [
    { to: "/integrations/automations", label: "Automações" },
    // Só o liga/desliga: as telas de operação do canal vivem no grupo
    // "Click to call".
    { to: "/settings/click-to-call", label: "Click to call" },
    { to: "/integrations/marketplace", label: "Marketplace" },
    { to: "/integrations/mcp", label: "Integração MCP" },
    { to: "/integrations/ingestion", label: "Ingestão" },
    // Configuração do tronco de saída. O diagnóstico (saúde, latência) continua
    // em Monitoramento › Troncos — aqui se muda, lá se acompanha.
    { to: "/integrations/telephony", label: "Telefonia" },
    // Veio de Auditoria: o log é do que as integrações fizeram, então mora
    // junto delas. A rota (/management/webhook-logs) não mudou.
    { to: "/management/webhook-logs", label: "Logs de integração" },
  ],
};

// O grupo só existe depois que a org habilita o recurso — desligado, ele
// ofereceria telas sem dado nenhum. A configuração continua alcançável em
// Integrações, que é por onde se liga.
export function clickToCallNavGroup(enabled: boolean): NavGroupDef | null {
  return enabled ? CLICK_TO_CALL : null;
}

// Configurações da org ativa. Era uma tela única (/settings) alcançável só pelo
// dropdown de perfil — virou grupo na navegação, uma rota por assunto, porque o
// atalho escondido não se achava e a tela crescia num scroll só.
export const CONFIGURACOES: NavGroupDef = {
  label: "Configurações",
  icon: Settings,
  items: [
    { to: "/settings/company", label: "Empresa" },
    { to: "/settings/brand", label: "Identidade da marca" },
    // A rota /settings/contact continua válida para links diretos, mas a tela
    // fica fora da navegação enquanto não for usada.
    { to: "/settings/hours", label: "Horário de atendimento" },
    { to: "/settings/dialer", label: "Discador" },
  ],
};

// Itens diretos (folhas de topo) da seção Operação.
export const DASHBOARD_LINK: NavLink = {
  to: "/dashboard",
  label: "Dashboard",
  icon: LayoutDashboard,
};
export const DISCADOR_LINK: NavLink = {
  to: "/dialer",
  label: "Discador automático",
  icon: PhoneOutgoing,
};
// Os números do próprio usuário. Todo papel vê (é "meu", não da equipe).
export const MEU_DESEMPENHO_LINK: NavLink = {
  to: "/my-performance",
  label: "Meu desempenho",
  icon: Gauge,
};
// "Discagem manual" (/phone): o teclado onde o vendedor digita um número solto.
// O fluxo padrão é o discador automático, então o item só entra na navegação
// (sidebar e drawer mobile) quando a marca liga a discagem manual em
// Configurações › Discador — ver manualDialNavLink abaixo.
export const DISCAGEM_MANUAL_LINK: NavLink = {
  to: "/phone",
  label: "Discagem manual",
  icon: Phone,
};

// Espelha clickToCallNavGroup: desligada, a tela só saberia recusar (POST
// /v1/calls responde manual_dial_disabled), então o item some do menu em vez de
// oferecer um teclado que não disca. A rota segue registrada para links diretos.
export function manualDialNavLink(enabled: boolean): NavLink | null {
  return enabled ? DISCAGEM_MANUAL_LINK : null;
}

// Painel da IA de Voz (config STT/LLM/TTS, vozes e script do lai-voice-talk).

// Seção Sistema.
export const CONTROL_DESK_LINK: NavLink = {
  to: "/control-desk",
  label: "Control Desk",
  icon: SlidersHorizontal,
};
export const SOBRE_LINK: NavLink = { to: "/about", label: "Sobre", icon: Info };
// Jornada de primeiro acesso: a checklist do que falta configurar na marca,
// derivada do banco. Acessível a todos os papéis — quem decide o conteúdo é o back.
export const JORNADA_LINK: NavLink = { to: "/journey", label: "Comece por aqui", icon: Compass };
// Ajuda: perguntas frequentes + assistente. Acessível a todos os papéis —
// quem mais precisa de ajuda é quem menos vê o resto do menu. Mantido
// exportado: outros arquivos (help-screen, o próprio grupo abaixo) usam.
export const AJUDA_LINK: NavLink = {
  to: "/support/help",
  label: "Central de Ajuda",
  icon: CircleQuestionMark,
};
// Meus chamados: mantido exportado pelo mesmo motivo. Acessível a todos os
// papéis.
export const TICKETS_LINK: NavLink = {
  to: "/support/tickets",
  label: "Meus chamados",
  icon: LifeBuoy,
};

// Suporte: a Ajuda (FAQ) e os chamados moram juntos porque são a mesma intenção
// — "preciso de ajuda". O assistente segue em desenvolvimento e, por enquanto,
// não é exposto como uma tela própria na navegação.
export const SUPORTE: NavGroupDef = {
  label: "Suporte",
  icon: LifeBuoy,
  items: [
    { to: AJUDA_LINK.to, label: AJUDA_LINK.label },
    { to: TICKETS_LINK.to, label: TICKETS_LINK.label },
  ],
};

// Destino que aparece direto na bottom bar mobile. O botão de menu (☰) abre o
// drawer com o restante. Usa o discador automático (não o manual): é o fluxo
// principal no celular. Rótulo curto p/ o chip.
export const BOTTOM_BAR_LINKS: NavLink[] = [
  { to: "/dialer", label: "Discador", icon: PhoneOutgoing },
  { to: "/my-performance", label: "Desempenho", icon: Gauge },
];

// Destinos que o vendedor NÃO vê na navegação (sidebar e drawer mobile): a
// tela dele é o discador, e esses itens só dispersam. Isto é ocultação de
// menu, não controle de acesso — as rotas seguem registradas e válidas para
// links diretos (quem restringe de verdade é lib/access.ts, pela permissão).
// Chave é o baseRole do viewer (GET /v1/access/me), não uma permissão: uma
// permissão bloquearia a rota, e a intenção aqui é só sumir do menu.
const OCULTOS_PARA_VENDEDOR = new Set(["/dashboard", "/journey"]);

type AccessBag = {
  catalog: PermissionCatalog | null;
  permissions: ReadonlySet<string>;
  crossBrand?: boolean;
  companyReports?: boolean;
  baseRole?: string | null;
};

// Um destino aparece na navegação quando a permissão libera E não está na
// lista de ocultos do vendedor. Fonte única para sidebar e bottom bar/drawer.
export function showInNav(access: AccessBag, to: LinkProps["to"] | string): boolean {
  const path = String(to);
  if (!canAccess({ ...access, path })) return false;
  if (OCULTOS_PARA_VENDEDOR.has(path) && access.baseRole === "seller") return false;
  return true;
}

// Filtra um grupo de navegação pela permissão: devolve o grupo só com os itens
// que a permissão libera, ou null se nenhum item sobrar (aí o grupo some da
// nav). Usa showInNav (não só canAccess) para que um item oculto do vendedor
// por OCULTOS_PARA_VENDEDOR continue escondido mesmo estando dentro de um grupo.
export function filterNavGroup(group: NavGroupDef, access: AccessBag): NavGroupDef | null {
  const items = group.items.filter((item) => showInNav(access, item.to));
  return items.length ? { ...group, items } : null;
}

// O subitem ativo é o de rota mais longa que casa por SEGMENTO: a tela de uma
// automação (/integrations/automations/$id) mantém "Automações" aceso, e um
// irmão que seja prefixo de outro não acende junto.
export function activeSubItem(items: { to?: string }[], pathname: string): string | null {
  let best: string | null = null;
  for (const { to } of items) {
    if (!to) continue;
    const casa = pathname === to || pathname.startsWith(to + "/");
    if (casa && (best === null || to.length > best.length)) best = to;
  }
  return best;
}
